import React, { useEffect } from 'react'
import { Field } from 'redux-form/immutable'
import StepWizard from 'react-step-wizard'

// @ts-ignore
import GlobalLoader from 'app/components/Loaders/GlobalLoader'
// @ts-ignore
import InfoBlock from 'app/components/InfoBlock'
// @ts-ignore
import { createArrayOfNumbers } from 'helpers/common'
import { REVIEWS_SURVEY_COMPLETED_PATH } from 'app/constants/navLinks'
import { EReviewsTypes, REVIEWS_USER_TYPES } from 'app/constants/reviews'
import OptionScaleStep from './OptionScaleStep'
import WelcomeStep from './WelcomeStep'
import WrapperStep from './WrapperStep'
import InputStep from './InputStep'
import ContactStep from './ContactStep'
import { INav, IReviewsSurveyQuestionsProps } from './interface'
// @ts-ignore
import { minLength, required } from 'helpers/validators'
import { handlePressEnter, substituteParticipantFirstNameAndLastName, trimmed } from 'helpers/reviews'

import './styles.scss'

const bc = 'reviews-survey-questions'
const isRequired = required()
const minLength2 = minLength(2)

const renderReviewersNomination = (
  { min, max }: { min: number[]; max: number[] },
  typeOfReviewer: string,
  getFormValue: (v: string) => string,
  invalid: boolean
) => {
  const minSize = min.length
  const maxSize = max.length
  return max.map((el: number, i: number) => {
    const reviewerStep = i + 1
    const isRequiredStep = reviewerStep <= minSize
    return (
      <WrapperStep invalid={invalid} withButton={false} key={`${typeOfReviewer}.${i}`}>
        <ContactStep
          title={`${typeOfReviewer.toUpperCase()} ${reviewerStep}/${maxSize} ${isRequiredStep ? '- Required' : ''}`}
          firstNameLabel="What is their first name?"
          firstNameField={`${typeOfReviewer}.${i}.firstName`}
          lastNameLabel="What is their last name?"
          lastNameField={`${typeOfReviewer}.${i}.lastName`}
          emailLabel={(field: string) => {
            const value = getFormValue(field)
            const v = value ? `${value}'s` : 'their'
            return `And what is ${v} work email address?`
          }}
          emailField={`${typeOfReviewer}.${i}.email`}
          disabled={invalid}
          isRequiredStep={isRequiredStep}
          skipBtnText={`I'm done adding ${typeOfReviewer.toUpperCase()}`}
          stepsForEndBlock={maxSize - i}
        />
      </WrapperStep>
    )
  })
}

const CoolNav = ({ totalSteps, currentStep, invalid, handleSubmit, nextStep }: INav) => {
  let handler: (e: any) => void = () => {}
  if (totalSteps === currentStep) {
    handler = handlePressEnter(handleSubmit, !invalid)
  } else if (nextStep) {
    handler = handlePressEnter(nextStep, !invalid)
  }
  useEffect(() => {
    // initiate the event handler
    document.addEventListener('keyup', handler)
    // this will clean up the event every time the component is re-rendered
    return function cleanup() {
      document.removeEventListener('keyup', handler)
    }
  })
  return <div />
}
const ReviewsSurveyQuestions: React.FC<IReviewsSurveyQuestionsProps> = ({
  handleSubmit,
  triggerReviewsSurveyProcess,
  survey,
  reviewsType,
  getFormValue,
  height,
  isSurveyForUser,
  invalid,
  location,
  surveyKeys,
  titleWelcomeText,
  titleInstructions,
  titleEndText,
  participantFirstName,
  participantLastName,
  participantEmail,
  change,
}) => {
  const isCompletedStep = location.pathname.includes(REVIEWS_SURVEY_COMPLETED_PATH)
  useEffect(() => {
    triggerReviewsSurveyProcess()
  }, [isCompletedStep])
  if (!survey.get('id')) {
    return <GlobalLoader />
  }
  if (!reviewsType || !survey) {
    return <InfoBlock>{'The survey link is invalid! Please contact to your Admin'}</InfoBlock>
  }
  const superiorsMaxArray = createArrayOfNumbers(survey.get('superiorMax'))
  const superiorsMinArray = createArrayOfNumbers(survey.get('superiorMin'))
  const isShowSuperiors = !!superiorsMaxArray.length
  const peersMaxArray = createArrayOfNumbers(survey.get('peerMax'))
  const peersMinArray = createArrayOfNumbers(survey.get('peerMin'))
  const isShowPeers = !!peersMaxArray.length
  const employeesMaxArray = createArrayOfNumbers(survey.get('employeeMax'))
  const employeesMinArray = createArrayOfNumbers(survey.get('employeeMin'))
  const isShowEmployees = !!employeesMaxArray.length
  if (isCompletedStep) {
    return (
      <div className={`${bc}__form`} style={{ height: `${height}px` }}>
        <WrapperStep invalid={invalid} title={titleEndText} withButton={false} isShowBackReturnButton={false} />
      </div>
    )
  }
  const isSurveyT1 = reviewsType === EReviewsTypes.t1
  return (
    <form
      className={`${bc}__form`}
      onSubmit={e => {
        e.preventDefault()
      }}
      style={{ height: `${height}px` }}
    >
      <StepWizard isLazyMount nav={<CoolNav invalid={invalid} handleSubmit={handleSubmit} />}>
        <WrapperStep btnText="Start Evaluation" invalid={invalid}>
          <WelcomeStep title={titleWelcomeText} />
        </WrapperStep>
        {isSurveyForUser && (
          <WrapperStep withButton={false} invalid={invalid}>
            <ContactStep
              title={survey.get('participantInfo')}
              firstNameLabel="What is your first name?"
              lastNameLabel="And what is your last name?"
              emailLabel="What is your email address (work email only)?"
              disabled={invalid}
            />
          </WrapperStep>
        )}
        {/* TODO: @Serginyo90 change reviewer name */}
        <WrapperStep
          invalid={invalid}
          title={isSurveyForUser ? 'Self-evaluation' : 'Leadership Feedback'}
          subtitle={titleInstructions}
          btnText="Continue"
        />
        {survey.get('competenciesQuestions').map((competencyQuestions: any, index: number) => {
          const path = 'competenciesQuestions.' + index
          return competencyQuestions.get('questions').map((question: any, i: number) => {
            const p = `${path}.${i}`
            const quest = substituteParticipantFirstNameAndLastName(
              question.get(surveyKeys.questionType),
              REVIEWS_USER_TYPES.USER,
              participantEmail,
              participantFirstName,
              participantLastName
            )
            return (
              <WrapperStep title={quest} withButton={false} key={p} invalid={invalid}>
                <OptionScaleStep name={p} change={change} getFormValue={getFormValue} />
              </WrapperStep>
            )
          })
        })}
        {survey.getIn(['openEndedQuestions', reviewsType])?.map((question: any, i: number) => {
          const path = `${reviewsType}.${i}`
          const quest = substituteParticipantFirstNameAndLastName(
            question.get(surveyKeys.questionType),
            REVIEWS_USER_TYPES.USER,
            participantEmail,
            participantFirstName,
            participantLastName
          )
          return (
            <Field
              label={quest}
              name={path}
              component={InputStep}
              key={path}
              validate={[isRequired, minLength2]}
              invalid={invalid}
              normalize={trimmed}
            />
          )
        })}

        {isSurveyT1 && isSurveyForUser && (
          <WrapperStep
            invalid={invalid}
            title={substituteParticipantFirstNameAndLastName(
              survey.get('nominationInstructions'),
              REVIEWS_USER_TYPES.USER,
              participantEmail,
              participantFirstName,
              participantLastName
            )}
            btnText="Continue"
          />
        )}
        {isSurveyT1 && isSurveyForUser && isShowSuperiors && (
          <WrapperStep
            title={substituteParticipantFirstNameAndLastName(
              survey.get('superiorInstructions'),
              REVIEWS_USER_TYPES.USER,
              participantEmail,
              participantFirstName,
              participantLastName
            )}
            btnText="Continue"
            invalid={invalid}
          />
        )}
        {isSurveyT1 &&
          isSurveyForUser &&
          isShowSuperiors &&
          renderReviewersNomination(
            { min: superiorsMinArray, max: superiorsMaxArray },
            'superiors',
            getFormValue,
            invalid
          )}
        {isSurveyT1 && isSurveyForUser && isShowPeers && (
          <WrapperStep
            title={substituteParticipantFirstNameAndLastName(
              survey.get('peerInstructions'),
              REVIEWS_USER_TYPES.USER,
              participantEmail,
              participantFirstName,
              participantLastName
            )}
            btnText="Continue"
            invalid={invalid}
          />
        )}
        {isSurveyT1 &&
          isSurveyForUser &&
          isShowPeers &&
          renderReviewersNomination({ min: peersMinArray, max: peersMaxArray }, 'peers', getFormValue, invalid)}
        {isSurveyT1 && isSurveyForUser && isShowEmployees && (
          <WrapperStep
            title={substituteParticipantFirstNameAndLastName(
              survey.get('employeeInstructions'),
              REVIEWS_USER_TYPES.USER,
              participantEmail,
              participantFirstName,
              participantLastName
            )}
            btnText="Continue"
            invalid={invalid}
          />
        )}
        {isSurveyT1 &&
          isSurveyForUser &&
          isShowEmployees &&
          renderReviewersNomination(
            { min: employeesMinArray, max: employeesMaxArray },
            'employees',
            getFormValue,
            invalid
          )}
        <WrapperStep
          invalid={invalid}
          title="Before submitting, please ensure the information provided has been entered correctly."
          btnText="Submit"
          customAction={() => handleSubmit()}
        />
      </StepWizard>
    </form>
  )
}

export default ReviewsSurveyQuestions
